import { BankType, BrandType, Country, GlobalSettingByEntity } from '../generated/graphql';

interface EntitySelectorInput {
  brand: BrandType;
  bank: BankType;
  country: Country;
}

export type DomainKey = keyof Omit<GlobalSettingByEntity, 'entitySelector'>;

type GetSettingByEntityOptions<K> = {
  settingsList: GlobalSettingByEntity[],
  input: EntitySelectorInput,
  domainKey: K,
};

/**
 * Retrieves the appropriate setting domain value from GlobalSettingByEntity
 * based on brand, bank, and country.
 *
 * Logic:
 * - If brand is TRANQUIAPP: Must match brand, bank, and country exactly
 * - If brand is NOT TRANQUIAPP:
 *   - First tries to find a match with the specific bank
 *   - If no match found, falls back to NO_BANK for that brand and country
 *
 * @param options - Configuration object containing:
 *   @param options.settingsList - Array of GlobalSettingByEntity from settings value
 *   @param options.input - Object containing brand, bank, and country
 *   @param options.domainKey - Domain key to retrieve (e.g., 'domainWhatsapp')
 * @returns The matched setting value for the specified domain, or null if no match found
 */
export function getGlobalSettingDomainByEntity<K extends DomainKey>(
  options: GetSettingByEntityOptions<K>,
): GlobalSettingByEntity[K] | null {
  const { settingsList, input, domainKey } = options;
  const { brand, bank, country } = input;

  if (brand === BrandType.Tranquiapp) {
    // For TRANQUIAPP, bank must match exactly
    const match = settingsList.find(
      (setting) => setting.entitySelector.brand === brand
        && setting.entitySelector.bank === bank
        && setting.entitySelector.country === country,
    );
    return match?.[domainKey] ?? null;
  }
  // For other brands, try to match with specific bank first
  const exactMatch = settingsList.find(
    (setting) => setting.entitySelector.brand === brand
        && setting.entitySelector.bank === bank
        && setting.entitySelector.country === country,
  );

  if (exactMatch) {
    return exactMatch[domainKey];
  }

  // If no exact bank match, fall back to NO_BANK
  const noBankMatch = settingsList.find(
    (setting) => setting.entitySelector.brand === brand
        && setting.entitySelector.bank === BankType.NoBank
        && setting.entitySelector.country === country,
  );

  return noBankMatch?.[domainKey] ?? null;
}
